import { computed, type ComputedRef } from 'vue'

import type { DashboardWidget } from '../models/widget.types'
import { partitionTabs } from '../service/partitionTabs'
import { widgetDefaultFilters, widgetPartition, widgetReportId } from '../service/widgetSpec'

import {
  resolvePartitionColumns,
  useLevelMetadata,
  useMetricCatalog,
  useReportSchedules,
} from '@/features/reports'

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
    : []
}

/**
 * The fields a rendered widget may offer beyond the ones it requests — its table settings, its
 * breakdown and its filters all choose from this set.
 *
 * v3 built every dashboard widget's columns from `getPartitionColumns` over the partition's whole
 * tab union, flagging only the chosen ones `selected` (`DataVisible.js setupTabs:46-130`). The v4
 * widget passed no `availableFields`, so `DataVisible` saw only the chosen fields: a table grouped
 * by date offered "Date" as its sole breakdown, and a saved `defaultBreakdown` such as
 * `marketing_channel` was discarded as unknown. v3 also kept the saved breakdown on offer
 * whatever the tabs said (`keepCol` → `defaultBreakdownFields`), so it is added explicitly here.
 *
 * Empty when the schedule carries no column list for the partition: `DataVisible` then falls back
 * to the requested fields, which is the old behaviour rather than an empty table.
 */
export function useWidgetFieldUniverse(
  widget: () => DashboardWidget,
  workspaceIds: () => readonly (number | string)[],
  isAccountCenter: () => boolean,
): ComputedRef<string[]> {
  const schedules = useReportSchedules()
  const catalog = useMetricCatalog()
  const levels = useLevelMetadata()

  return computed(() => {
    const wire = widget().wire
    const reportId = widgetReportId(wire)
    const partition = widgetPartition(wire)
    if (reportId === undefined || !partition) return []
    const ids = workspaceIds()
    const tabs = (ids.length ? ids : [undefined]).flatMap((id) =>
      partitionTabs(
        schedules.getScheduleByReportId(reportId, id === undefined ? undefined : [id]),
        partition,
      ),
    )
    if (!tabs.some((tab) => Array.isArray(tab.columns) && tab.columns.length > 0)) return []

    const dimensions = [...strings(wire.setDimensions), ...strings(wire.chartDimensions)]
    const metrics = [...strings(wire.setMetrics), ...strings(wire.chartMetrics)]
    const resolved = resolvePartitionColumns({
      tabs,
      dimensions,
      metrics,
      partitionName: partition,
      filters: widgetDefaultFilters(wire),
      isAccountCenter: isAccountCenter(),
      workspaceIds: ids,
      getColumn: catalog.getColumn,
    })
    const disabled = new Set(resolved.disabledFields)
    const breakdown = typeof wire.defaultBreakdown === 'string' ? [wire.defaultBreakdown] : []
    return [
      ...new Set([
        ...resolved.fields.filter((field) => !disabled.has(field)),
        ...breakdown,
        ...levels.runtimeMetricFields(partition),
      ]),
    ]
  })
}
